import { ReceivingPreprocessProfile } from './fixtures'

export type PackLevel = 'each' | 'inner' | 'carton' | 'pallet'
export type MeasurementField = 'quantity' | 'ti' | 'hi' | 'length' | 'width' | 'height' | 'weight'
export type PackValues = Partial<Record<MeasurementField, number>>
export interface PackMeasurement {
  values: PackValues
  photos: string[]
  absent: boolean
  complete: boolean
}
export interface MeasurementState {
  level: PackLevel
  field: MeasurementField
  input: string
  photo: boolean
  dialog: 'difference' | 'absence' | null
  error: string | null
  packs: Record<PackLevel, PackMeasurement>
}
export type MeasurementAction =
  | { type: 'set-measurement'; value: string }
  | { type: 'submit-measurement' }
  | { type: 'confirm-measurement-update' }
  | { type: 'cancel-measurement-dialog' }
  | { type: 'skip-pack' }
  | { type: 'add-measurement-photos'; names: string[] }
  | { type: 'retake-measurement-photos' }
  | { type: 'accept-measurement-photos' }
  | { type: 'confirm-pack-absence' }
  | { type: 'measurement-back' }

export const packLevels: PackLevel[] = ['each', 'inner', 'carton', 'pallet']
export const fieldLabels: Record<MeasurementField, string> = {
  quantity: 'Quantity',
  ti: 'Ti',
  hi: 'Hi',
  length: 'Length',
  width: 'Width',
  height: 'Height',
  weight: 'Weight',
}
export const packLabels: Record<PackLevel, string> = {
  each: 'Each',
  inner: 'Inner pack',
  carton: 'Carton',
  pallet: 'Pallet',
}
const emptyPack = (): PackMeasurement => ({ values: {}, photos: [], absent: false, complete: false })
export const createMeasurements = (): MeasurementState => ({
  level: 'each',
  field: 'length',
  input: '',
  photo: false,
  dialog: null,
  error: null,
  packs: { each: emptyPack(), inner: emptyPack(), carton: emptyPack(), pallet: emptyPack() },
})
export const measurementFields = (level: PackLevel): MeasurementField[] => [
  ...(level === 'each' ? [] : ['quantity' as const]),
  ...(level === 'pallet' ? ['ti' as const, 'hi' as const] : []),
  'length',
  'width',
  'height',
  'weight',
]
export const parentLevel = (state: MeasurementState, level = state.level): PackLevel => {
  if (level === 'pallet' && !state.packs.carton.absent) return 'carton'
  if ((level === 'pallet' || level === 'carton') && !state.packs.inner.absent) return 'inner'
  return 'each'
}
export const measurementUnit = (state: MeasurementState): string => {
  const child = parentLevel(state)
  const countUnit = child === 'each' ? 'eaches' : child === 'inner' ? 'inner packs' : 'cartons'
  if (state.field === 'quantity') return countUnit
  if (state.field === 'ti') return `${countUnit} per layer`
  if (state.field === 'hi') return 'layers'
  return state.field === 'weight' ? 'lb' : 'in'
}
export const storedMeasurement = (
  state: MeasurementState,
  profile: ReceivingPreprocessProfile | null
): number | null => {
  if (!profile) return null
  const { level, field } = state
  if (field === 'quantity') {
    if (level === 'each') return 1
    const parent = parentLevel(state)
    return profile[level].units / (parent === 'each' ? 1 : profile[parent].units)
  }
  if (field === 'ti' || field === 'hi') return profile.pallet[field]
  return level === 'each' ? profile.each[field] : profile[level].dimensions[field]
}
export const measuredProfile = (state: MeasurementState): ReceivingPreprocessProfile => {
  const dimensions = (level: PackLevel) => {
    const v = state.packs[level].values
    return { length: v.length ?? 0, width: v.width ?? 0, height: v.height ?? 0, weight: v.weight ?? 0 }
  }
  const innerUnits = state.packs.inner.absent ? 1 : (state.packs.inner.values.quantity ?? 0)
  const cartonUnits = state.packs.carton.absent ? innerUnits : (state.packs.carton.values.quantity ?? 0) * innerUnits
  return {
    each: dimensions('each'),
    inner: { units: innerUnits, dimensions: dimensions('inner') },
    carton: { units: cartonUnits, dimensions: dimensions('carton') },
    pallet: {
      units: (state.packs.pallet.values.quantity ?? 0) * cartonUnits,
      ti: state.packs.pallet.values.ti ?? 0,
      hi: state.packs.pallet.values.hi ?? 0,
      dimensions: dimensions('pallet'),
    },
  }
}

const validatePallet = (state: MeasurementState): string | null => {
  const p = state.packs.pallet.values
  const child = state.packs[parentLevel(state, 'pallet')].values
  if (p.quantity !== (p.ti ?? 0) * (p.hi ?? 0))
    return 'Pallet quantity must equal Ti × Hi. Re-enter the pallet quantity and layer counts.'
  const fit = Math.max(
    Math.floor((p.length ?? 0) / (child.length ?? 1)) * Math.floor((p.width ?? 0) / (child.width ?? 1)),
    Math.floor((p.length ?? 0) / (child.width ?? 1)) * Math.floor((p.width ?? 0) / (child.length ?? 1))
  )
  if ((p.ti ?? 0) > fit) return 'The entered layer count does not fit the pallet footprint.'
  if ((p.hi ?? 0) * (child.height ?? 0) > (p.height ?? 0)) return 'The stack exceeds the entered pallet height.'
  if ((p.quantity ?? 0) * (child.weight ?? 0) > (p.weight ?? 0)) return 'The contents exceed the entered pallet weight.'
  if (
    (p.quantity ?? 0) * (child.length ?? 0) * (child.width ?? 0) * (child.height ?? 0) >
    (p.length ?? 0) * (p.width ?? 0) * (p.height ?? 0)
  )
    return 'The contents exceed the entered pallet volume.'
  return null
}

const saveMeasurement = (state: MeasurementState): MeasurementState => {
  const packs = {
    ...state.packs,
    [state.level]: {
      ...state.packs[state.level],
      values: { ...state.packs[state.level].values, [state.field]: Number(state.input) },
      complete: false,
    },
  }
  // Editing a measurement invalidates later levels and their physical evidence.
  for (const level of packLevels.slice(packLevels.indexOf(state.level) + 1)) packs[level] = emptyPack()
  const next = { ...state, packs, dialog: null, error: null }
  const fields = measurementFields(state.level)
  const field = fields[fields.indexOf(state.field) + 1]
  if (field) return { ...next, field, input: String(packs[state.level].values[field] ?? '') }
  const error = state.level === 'pallet' ? validatePallet(next) : null
  if (error) return { ...next, field: 'quantity', input: String(packs.pallet.values.quantity ?? ''), error }
  return { ...next, photo: true, input: '' }
}

const completeLevel = (state: MeasurementState): MeasurementState => {
  const packs = { ...state.packs, [state.level]: { ...state.packs[state.level], complete: true } }
  const level = packLevels[packLevels.indexOf(state.level) + 1]
  if (!level) return { ...state, packs, dialog: null }
  return { ...state, packs, level, field: 'quantity', input: '', photo: false, dialog: null, error: null }
}

export const reduceMeasurement = (
  state: MeasurementState,
  action: MeasurementAction,
  profile: ReceivingPreprocessProfile | null
): MeasurementState => {
  switch (action.type) {
    case 'set-measurement':
      return { ...state, input: action.value, error: null }
    case 'submit-measurement': {
      const value = Number(state.input)
      if (
        !state.input.trim() ||
        !Number.isFinite(value) ||
        value <= 0 ||
        (['quantity', 'ti', 'hi'].includes(state.field) && !Number.isSafeInteger(value))
      ) {
        return {
          ...state,
          error: 'Enter a positive value' + (['quantity', 'ti', 'hi'].includes(state.field) ? ' in whole units.' : '.'),
        }
      }
      const stored = storedMeasurement(state, profile)
      return stored !== null && value !== stored ? { ...state, dialog: 'difference' } : saveMeasurement(state)
    }
    case 'confirm-measurement-update':
      return state.dialog === 'difference' ? saveMeasurement(state) : state
    case 'cancel-measurement-dialog':
      return state.dialog === 'absence'
        ? {
            ...state,
            dialog: null,
            photo: false,
            field: 'quantity',
            input: '',
            packs: { ...state.packs, [state.level]: { ...state.packs[state.level], absent: false, complete: false } },
          }
        : { ...state, dialog: null }
    case 'skip-pack': {
      if (state.level === 'each' || state.level === 'pallet' || state.field !== 'quantity') return state
      const packs = { ...state.packs, [state.level]: { ...emptyPack(), absent: true } }
      for (const level of packLevels.slice(packLevels.indexOf(state.level) + 1)) packs[level] = emptyPack()
      return { ...state, packs, photo: true, error: null }
    }
    case 'add-measurement-photos':
      return state.photo
        ? {
            ...state,
            error: null,
            packs: {
              ...state.packs,
              [state.level]: {
                ...state.packs[state.level],
                photos: [...state.packs[state.level].photos, ...action.names],
              },
            },
          }
        : state
    case 'retake-measurement-photos':
      return { ...state, packs: { ...state.packs, [state.level]: { ...state.packs[state.level], photos: [] } } }
    case 'accept-measurement-photos':
      if (!state.photo) return state
      if (!state.packs[state.level].photos.length) return { ...state, error: 'Capture photo evidence to continue.' }
      return state.packs[state.level].absent ? { ...state, dialog: 'absence' } : completeLevel(state)
    case 'confirm-pack-absence':
      return state.dialog === 'absence' ? completeLevel(state) : state
    case 'measurement-back': {
      if (state.dialog) return reduceMeasurement(state, { type: 'cancel-measurement-dialog' }, profile)
      if (state.photo) {
        const field = state.packs[state.level].absent ? 'quantity' : 'weight'
        return {
          ...state,
          photo: false,
          field,
          input: String(state.packs[state.level].values[field] ?? ''),
          error: null,
          packs: { ...state.packs, [state.level]: { ...state.packs[state.level], absent: false, complete: false } },
        }
      }
      const fields = measurementFields(state.level)
      const previous = fields[fields.indexOf(state.field) - 1]
      if (previous)
        return {
          ...state,
          field: previous,
          input: String(state.packs[state.level].values[previous] ?? ''),
          error: null,
        }
      const level = packLevels[packLevels.indexOf(state.level) - 1]
      return level
        ? {
            ...state,
            level,
            photo: true,
            dialog: null,
            error: null,
            packs: { ...state.packs, [level]: { ...state.packs[level], complete: false } },
          }
        : state
    }
  }
}
